import {
  useGetArtworkSets,
  useUpdateEnsembleMenuOrder,
} from "@/api/ensemble/ensemble";
import { Ensemble } from "@/lib/types";
import { isValidElement, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAdditionalFilters } from "@/context/additional-filters/AdditionalFiltersContext";
import {
  Sortable,
  SortableDragHandle,
  SortableItem,
} from "@/components/ui/sortable";
import { MenuItem } from "../ui/menuitem";
import { DragHandleDots2Icon } from "@radix-ui/react-icons";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

type ArtworkSetsMenuSectionProps = {
  title: React.ReactNode;
  visibility: Ensemble["visibility"];
};

const ArtworkSetsMenuSection = ({
  title,
  visibility,
}: ArtworkSetsMenuSectionProps) => {
  const vt = useVisibleT();
  const filters = useAdditionalFilters();
  const location = useLocation();
  const { data: sets } = useGetArtworkSets({ visibility });
  const updateMenuOrder = useUpdateEnsembleMenuOrder();

  const visibilityValue = visibility ?? "private";

  /** Order comes from API (`EnsembleMenuOrder`); see `GET back/api/ensemble`. */
  const orderedSets = useMemo(() => (sets?.length ? [...sets] : []), [sets]);

  const isActive = useCallback(
    (pageSetId: string) => filters?.setId === pageSetId,
    [filters?.setId],
  );

  const handleValueChange = useCallback(
    (newOrdered: Ensemble[]) => {
      const ids = newOrdered.map((s) => s.id);
      updateMenuOrder.mutate({ visibility: visibilityValue, orderedIds: ids });
    },
    [visibilityValue, updateMenuOrder],
  );

  if (!orderedSets.length) {
    return (
      <div className="flex flex-col">
        {isValidElement(title) ? (
          title
        ) : (
          <span className="py-3 px-6 font-bold text-sm">{title}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {isValidElement(title) ? (
        title
      ) : (
        <span className="py-3 px-6 font-bold text-sm">{title}</span>
      )}

      <Sortable value={orderedSets} onValueChange={handleValueChange}>
        {orderedSets.map((set) => (
          <SortableItem key={set.id} value={set.id}>
            <div className="flex items-center w-full min-w-0">
              <SortableDragHandle
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                aria-label={vt("bo.nav.reorder", "Réordonner")}
              >
                <DragHandleDots2Icon className="size-4" aria-hidden />
              </SortableDragHandle>
              <div className="flex-1 min-w-0">
                <MenuItem
                  to={{
                    search: location.search,
                  }}
                  onClick={() => {
                    if (set.id === filters?.setId) {
                      filters?.setSetId(undefined);
                      return;
                    }
                    filters?.setSetId(set.id);
                  }}
                  number={set.artworkCount}
                  name={set.ensemblesDescriptionNomEnsemble}
                  forcedActive={isActive(set.id)}
                />
              </div>
            </div>
          </SortableItem>
        ))}
      </Sortable>
    </div>
  );
};

export default ArtworkSetsMenuSection;
